<template>
  <v-btn :class="[$style.baseBtn, ButtonStyle, disabledStyle]"
         v-bind="$attrs"
         flat
         v-on="$listeners">
    <slot/>
  </v-btn>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';

@Component({
  name:  'BaseButton',
  props: {
    buttonStyle: String,
  },
})
export default class BaseButton extends Vue {
  get disabledStyle() {
    return this.$attrs.disabled ? this.$style.disabled : '';
  }

  get ButtonStyle() {
    switch (this.buttonStyle) {
      case ('Main'):
        return this.$style.mainBtn;

      case ('Back'):
        return this.$style.backBtn;

      case ('Alpha'):
        return this.$style.alphaBtn;

      case ('Round'):
        return this.$style.roundBtn;

      case ('RoundCentered'):
        return this.$style.roundCenteredBtn;

      case ('Cancel'):
        return this.$style.cancelBtn;

      case ('Danger'):
        return this.$style.dangerBtn;

      case ('Teal'):
        return this.$style.tealBtn;

      default:
        return false;
    }
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";

$cancel-button-color: #DEDEDE;
$cancel-button-pressed: #CDCDCD;
$cancel-button-hover: #EEE;

.baseBtn {
  min-width: 0;
  height: auto;

  div {
    width: auto;
    font-family: 'Gotham', sans-serif;
    font-size: 24px;
    line-height: 24px;
    font-weight: 700;
    text-transform: uppercase;
    outline: none;
    transition: all 0.25s ease-in-out;
  }

  &.disabled {
    opacity: 0.5;
  }
}

.baseBtn.mainBtn {
  border-radius: 37.5px;
  background: linear-gradient(180deg, #58C762 0%, #16961E 100%);
  display: flex;
  justify-content: center;
  align-items: center;

  div {
    color: $white;
    width: auto;
  }

  &:hover,
  &:focus {
    background: linear-gradient(180deg, #67F074 0%, #0FA619 100%);
  }

  &:active {
    background: linear-gradient(180deg, #3DA747 0%, #0A7111 100%);
  }
}

.baseBtn.backBtn {
  border: 2px solid $green-color;
  border-radius: 37.5px;
  display: flex;
  justify-content: center;
  align-items: center;

  div {
    color: $black-slightly-transparent;
  }

  &:hover,
  &:focus {
    background-color: $gray-light;
  }

  &:active {
    background-color: $gray-dark;
  }
}

.baseBtn.alphaBtn {
  height: auto;
  background-color: $black-light-transparent;
  border: 2px solid $green-color;
  box-shadow: 0 -1px 4px 0 $black-semi-transparent;
  margin: 0 28px 36px;
  width: calc(50% - 56px);
  padding: 20px 0;
  position: relative;

  div {
    color: $white;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:hover,
  &:focus {
    background-color: $black-transparent;
  }

  &:active {
    background-color: $black-semi-transparent;
  }

  &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 40px;
    top: calc(50% - 20px);
    right: 26px;
    background-image: url("/static/add.png");
    background-position: center;
    background-size: contain;
  }
}

.baseBtn.roundBtn {
  width: 145px;
  height: 145px;
  border-radius: 50%;
  border: 2px solid $white;
  padding: 0;
  display: flex;

  &:not(:last-child) {
    margin-right: 36px;
  }

  div {
    width: 145px;
    height: 145px;
    background-color: transparent;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 39px 20px 29px;
    align-items: center;
    color: $white;
    font-size: 15px;
  }

  &::before {
    content: none;
  }
}

.baseBtn.roundCenteredBtn {
  width: 145px;
  height: 145px;
  border-radius: 50%;
  border: 2px solid $white;
  padding: 0;
  display: flex;

  div {
    width: 145px;
    height: 145px;
    background-color: transparent;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 39px 20px;
    align-items: center;
    color: $white;
    font-size: 15px;
  }

  &::before {
    content: none;
  }
}

.baseBtn.cancelBtn {
  padding: 17px 76px;
  border-radius: 30px;
  background-color: $cancel-button-color;

  div {
    color: $black-slightly-transparent;
    font-size: 17px;
    font-weight: 700;
  }

  &:hover,
  &:focus {
    background-color: $cancel-button-hover;
  }

  &:active {
    background-color: $cancel-button-pressed;
  }
}

.baseBtn.dangerBtn {
  padding: 17px 28px;
  border-radius: 30px;
  background: linear-gradient(180deg, #FF5050 0%, #DE1E1E 100%);

  div {
    color: $white;
    font-size: 17px;
    font-weight: 700;
  }

  &:hover,
  &:focus {
    background: linear-gradient(180deg, #FF7171 0%, #FF3D3D 100%);
  }

  &:active {
    background: linear-gradient(180deg, #FF3131 0%, #B71A1A 100%);
  }
}

.baseBtn.tealBtn {
  background-color: transparent;
  outline: none;
  padding: 10px;
  margin: 0;

  div {
    color: $teal;
    font-size: 14px;
    text-transform: uppercase;
  }
}

@media screen and (max-width: 1180px) {
  .baseBtn div {
    font-size: 20px;
  }
}

@media screen and (max-width: $kiosk-breakpoint-max) {
  .baseBtn.alphaBtn {
    margin: 0 0 16px;
    width: 100%;
    padding: 29px 0;
    background-color: transparent;
    box-shadow: none;
    border: 3px solid $green-color;

    div {
      color: $black;
      font-size: 30px;
      line-height: 38px;
    }

    &:hover,
    &:focus,
    &:active {
      background-color: transparent;
    }

    &::after {
      width: 54px;
      height: 54px;
      background-image: url("/static/add-black.png");
      right: 16px;
      top: calc(50% - 27px);
    }
  }
}

@media screen and (max-width: 965px) {
  .baseBtn.alphaBtn {
    padding: 17px 0;
    border: 1px solid $green-color;
    border-radius: 2px;
    margin-bottom: 5px;

    div {
      font-size: 14px;
      line-height: 16px;
    }

    &:after {
      width: 28px;
      height: 28px;
      top: calc(50% - 14px);
    }
  }
}

@media screen and (max-width: 560px) {
  .baseBtn.cancelBtn {
    width: 50%;
    padding: 11px 0;
    margin: 0;
    background-color: transparent;
    border-radius: 0;
    border-right: 1px solid $modals-border;

    div {
      color: $blue2;
      text-transform: none;
    }

    &:hover,
    &:focus {
      background: transparent;
    }

    &:active {
      background: transparent;
    }
  }
  .baseBtn.dangerBtn {
    width: 50%;
    padding: 11px 0;
    margin: 0;
    background: transparent;
    border-radius: 0;

    div {
      color: $blue2;
      text-transform: none;
    }

    &:hover,
    &:focus {
      background: transparent;
    }

    &:active {
      background: transparent;
    }
  }
  .baseBtn.tealBtn {
    width: 100%;
    border-top: 1px solid $modals-border;

    div {
      color: $blue2;
      font-weight: 700;
      font-size: 17px;
      text-transform: none;
    }
  }
}

@media screen and (max-width: 480px) {
  .baseBtn.roundBtn {
    width: 100px;
    height: 100px;

    div {
      width: 100px;
      height: 100px;
      padding: 0;
      justify-content: center;
      font-size: 10px;
    }
  }

  .baseBtn div {
    font-size: 10px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BaseButton.vue